import { Presence } from '@chakra-ui/react/presence';
import { ComponentProps } from 'react';
import { create } from 'zustand';
import { combine } from 'zustand/middleware';

import CIConfigExpert from '@/components/CIConfigExpert/CIConfigExpert';
import MultiTurnFixer from '@/components/MultiTurnSidebar/MultiTurnFixer';
import postMessageToWorkflowEditor from '@/pages/WorkflowEditorPage/utils/postMessageToWorkflowEditor';

type VariantState<V extends string, P extends object> = {
  variant: V;
  props: P;
};

type MultiTurnState = VariantState<
  'multi-turn',
  Omit<ComponentProps<typeof MultiTurnFixer>, 'onClose' | 'onHide' | 'onUpdate'>
>;

type CIConfigExpertState = VariantState<'ci-config-expert', Omit<ComponentProps<typeof CIConfigExpert>, 'onHide'>>;

type SidebarState = MultiTurnState | CIConfigExpertState;

type VariantProps<K extends SidebarState['variant']> = Extract<SidebarState, { variant: K }>['props'];

const useOverlaySidebarStore = create(
  combine(
    {
      state: { variant: 'none' } as SidebarState | { variant: 'none' },
      visibility: 'closed' as 'visible' | 'closed' | 'hidden' | 'immediate',
    },
    (set, get) => {
      const methods = {
        afterAnimated() {
          const { visibility: vis, state } = get();
          if (vis === 'hidden' || vis === 'closed') {
            if (window.Intercom) {
              window.Intercom('update', { horizontal_padding: '' });
            } else {
              window.intercomSettings = { horizontal_padding: '' };
            }
          }
          if (vis === 'closed') {
            if (state.variant === 'ci-config-expert') {
              postMessageToWorkflowEditor('CI_CONFIG_EXPERT_CLOSED');
            }
            set({ state: { variant: 'none' } });
          }
        },
        update<K extends SidebarState['variant']>(props: Partial<VariantProps<K>>, variant: K) {
          const current = get().state;
          if (current.variant === variant) {
            const typedCurrent = current as Extract<SidebarState, { variant: K }>;
            set({
              state: {
                ...typedCurrent,
                props: { ...typedCurrent.props, ...props } as unknown as VariantProps<K>,
              },
            });
          }
        },
        hide() {
          set({ visibility: 'hidden' });
        },
        close() {
          set({ visibility: 'closed' });
        },
        show(state: SidebarState, opts?: { immediate?: boolean }) {
          if (window.Intercom) {
            window.Intercom('update', { horizontal_padding: 420 });
          } else {
            window.intercomSettings = { horizontal_padding: 420 };
          }
          set({ state, visibility: opts?.immediate ? 'immediate' : 'visible' });
        },
      };
      return methods;
    },
  ),
);

export const closeOverlaySidebar = () => {
  useOverlaySidebarStore.setState({
    state: { variant: 'none' },
    visibility: 'closed',
  });
};

export const useOverlaySidebar = () => {
  const { show, hide, visibility, state } = useOverlaySidebarStore();
  return { show, hide, visibility, state };
};

const OverlaySidebar = () => {
  const { state, hide, close, update, afterAnimated, visibility } = useOverlaySidebarStore();

  let node;
  if (state.variant === 'multi-turn') {
    node = (
      <MultiTurnFixer
        onClose={close}
        onUpdate={(props) => update(props, state.variant)}
        onHide={hide}
        {...state.props}
      />
    );
  }
  if (state.variant === 'ci-config-expert') {
    node = <CIConfigExpert {...state.props} onHide={hide} />;
  }
  return (
    <Presence
      present={visibility === 'visible' || visibility === 'immediate'}
      animationName={{ _open: 'slide-from-right-full', _closed: 'slide-to-right-full' }}
      animationDuration={visibility === 'immediate' ? '0s' : 'slow'}
      onExitComplete={afterAnimated}
      position="fixed"
      top="0"
      bottom="0"
      right="0"
      width="100%"
      zIndex={1250}
      pointerEvents="none"
    >
      {node}
    </Presence>
  );
};

export default OverlaySidebar;
